import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Image,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import ImageLoaderHandler from "@raiden/library/components/ImageLoaderHandler";
import { ApiErrorProvider } from "@raiden/library/components/ReactHookForm/ApiError";
import { useConfiguration } from "@raiden/library/hooks/useConfiguration";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { CallButton } from "../../../components/CallButton";
import CenteredContent from "../../../components/CenteredContent";
import { Icon } from "../../../components/Icon";
import { PageLayout } from "../../../components/PageLayout";
import { SocialLink } from "../../../components/SocialLink";
import { Splash } from "../../../components/Splash";
import { SOCIAL_LINKS } from "../../../constants/contact";
import { ContactsCreateForm } from "./Form";
import ContactsCreateHandler from "./Handler";

export function Contact() {
  const intl = useIntl();

  const color = useColorModeValue("brandPrimary.400", "brandPrimary.400");

  const [isSuccess, setIsSuccess] = useState(false);

  const { configuration } = useConfiguration();

  const email = configuration.environments[0]?.email;

  return (
    <PageLayout
      title={intl.formatMessage({ defaultMessage: "Nous contacter" })}
      description={intl.formatMessage({
        defaultMessage: "Prenez rendez-vous avec votre conseiller",
      })}>
      <Box pb="4rem">
        <CenteredContent maxWidth="calc(1320px)">
          <Stack direction={{ base: "column", lg: "row" }} spacing="1.5rem">
            <GoogleReCaptchaProvider
              reCaptchaKey={
                process.env.NEXT_PUBLIC_RECAPTCHA_V3_PUBLIC_KEY ?? ""
              }>
              <ApiErrorProvider>
                <ContactsCreateHandler
                  onSuccess={() => {
                    setIsSuccess(true);
                  }}>
                  {({ form: { reset, control }, isLoading }) => (
                    <Box
                      flexShrink={0}
                      backgroundColor="brandPrimary.100"
                      p="1.5rem">
                      <Box position="relative">
                        {process.env.NEXT_PUBLIC_RECAPTCHA_V3_PUBLIC_KEY ? (
                          <Box visibility={isSuccess ? "hidden" : "visible"}>
                            <Text variant="h3">
                              <FormattedMessage defaultMessage="Un conseiller vous appelle" />
                            </Text>

                            <ContactsCreateForm
                              control={control}
                              isLoading={isLoading}
                            />
                          </Box>
                        ) : (
                          <Alert status="error">
                            <AlertTitle>
                              <FormattedMessage defaultMessage="Erreur de configuration" />
                            </AlertTitle>

                            <AlertDescription>
                              <FormattedMessage defaultMessage="La clé publique de reCAPTCHA n'est pas configurée." />
                            </AlertDescription>
                          </Alert>
                        )}

                        <Flex
                          display={isSuccess ? "flex" : "none"}
                          position="absolute"
                          top="0"
                          left="0"
                          right="0"
                          bottom="0"
                          alignItems="center"
                          justifyContent="center">
                          <Splash
                            src={generateFrontPath({
                              id: "@front.internal-assets",
                              parameters: {
                                filePath: "illustrations/contact-success.svg",
                              },
                              includeBasePath: true,
                            })}
                            title={intl.formatMessage({
                              defaultMessage:
                                "Nous avons bien reçu votre demande",
                            })}
                            description={intl.formatMessage({
                              defaultMessage:
                                "Un conseiller vous répondra au plus vite, merci.",
                            })}
                            addon={
                              <Center>
                                <Button
                                  onClick={() => {
                                    reset();
                                    setIsSuccess(false);
                                  }}
                                  colorScheme="brandPrimary">
                                  <FormattedMessage defaultMessage="Revenir au formulaire" />
                                </Button>
                              </Center>
                            }
                          />
                        </Flex>
                      </Box>
                    </Box>
                  )}
                </ContactsCreateHandler>
              </ApiErrorProvider>
            </GoogleReCaptchaProvider>

            <Box
              flexShrink={0}
              w={{ base: "full", lg: "35%" }}
              px={{ base: "1rem", lg: "0" }}>
              <Stack>
                <CallButton
                  message={defineMessage({
                    defaultMessage: "Par téléphone au{br}{phone}",
                  })}
                />
              </Stack>

              <Box>
                <ImageLoaderHandler
                  src={generateFrontPath({
                    id: "@front.internal-assets",
                    parameters: { filePath: "illustrations/contact.svg" },
                    includeBasePath: true,
                  })}>
                  {({ src, handleLoad, isLoaded, ref }) => (
                    <Image
                      ref={ref}
                      onLoad={handleLoad}
                      transition="opacity 0.25s ease"
                      opacity={isLoaded ? 1 : 0}
                      src={src}
                      alt={intl.formatMessage({
                        defaultMessage: "contact",
                      })}
                    />
                  )}
                </ImageLoaderHandler>
              </Box>

              <Box mt="1.5rem">
                <HStack alignItems="flex-start">
                  <Icon icon="ms_check" size="1.5rem" color={color} />

                  <Text color="brandPrimary.400">
                    <FormattedMessage defaultMessage="Simple, gratuit et sans engagement" />
                  </Text>
                </HStack>

                <HStack alignItems="flex-start">
                  <Icon icon="ms_check" size="1.5rem" color={color} />

                  <Text color="brandPrimary.400">
                    <FormattedMessage defaultMessage="Vous économisez du temps et de l'argent" />
                  </Text>
                </HStack>

                <HStack alignItems="flex-start">
                  <Icon icon="ms_check" size="1.5rem" color={color} />

                  <Text color="brandPrimary.400">
                    <FormattedMessage defaultMessage="Accompagnement à vie" />
                  </Text>
                </HStack>

                <HStack alignItems="flex-start">
                  <Icon icon="ms_check" size="1.5rem" color={color} />

                  <Text color="brandPrimary.400">
                    <FormattedMessage defaultMessage="Votre espace privé pour gérer tous vos contrats et fournisseurs" />
                  </Text>
                </HStack>
              </Box>

              <Divider
                borderBottomWidth="2px"
                borderColor="brandPrimary.200"
                mt="1.5rem"
              />

              <Text variant="text-large" color="brandPrimary.400" mt="16px">
                <FormattedMessage defaultMessage="Nous contacter" />
              </Text>

              <Stack spacing="1rem" alignItems="flex-start" w="full" mt="1rem">
                {SOCIAL_LINKS.map((socialLink, index) => (
                  <SocialLink
                    {...socialLink}
                    color="brandPrimary.400"
                    key={index}
                  />
                ))}

                {email && (
                  <SocialLink
                    src={generateFrontPath({
                      id: "@front.internal-assets",
                      parameters: { filePath: "icons/send.svg" },
                    })}
                    alt={defineMessage({ defaultMessage: "email" })}
                    href={`mailto:${email}`}
                    label={email}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </CenteredContent>
      </Box>
    </PageLayout>
  );
}
